import { useMemo, useState } from "react";
import { ArrowUpRight, Check, Plus } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { usePlanUi, type PlanKey } from "@/hooks/usePlanUi";
import { usePlanUpgrade } from "@/components/plan/PlanUpgradeDialog";
import { useLanguage } from "@/i18n/LanguageContext";

const euros = (c: number) => (c / 100).toLocaleString("es-ES", { maximumFractionDigits: 2 });

/**
 * Botón compacto "Mejora tu plan" con pestañas para cada plan superior y sus
 * ventajas. Solo presentación: el cambio usa el flujo de upgrade existente.
 */
export default function UpgradePlansMenu() {
  const ui = usePlanUi();
  const { t, tArr } = useLanguage();
  const { requestUpgrade, dialog } = usePlanUpgrade();
  const [open, setOpen] = useState(false);
  const currentRank = ui.plan?.rank ?? 0;
  const higher = useMemo(
    () => ui.plans.filter((p) => p.rank > currentRank),
    [ui.plans, currentRank],
  );
  const [tab, setTab] = useState<string | null>(null);
  if (!ui.active || !ui.plan || higher.length === 0) return null;
  const selected = higher.find((p) => p.plan_key === tab) ?? higher[0];
  if (!selected) return null;
  const benefits = tArr("upgradePlans", selected.plan_key);

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button type="button" size="sm" className="h-9 shrink-0 gap-1.5 rounded-full px-3 text-xs font-bold">
            {t("upgradePlans", "trigger")}
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" className="max-h-[calc(100dvh-96px)] w-[min(340px,calc(100vw-24px))] overflow-y-auto rounded-2xl p-3">
          <p className="px-1 font-body text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            {t("upgradePlans", "current").replace("{plan}", ui.planName(ui.planKey))}
            {ui.previewOn && t("upgradePlans", "preview")}
          </p>
          <div role="tablist" className="mt-2 flex gap-1 rounded-full border border-border bg-secondary/40 p-0.5">
            {higher.map((p) => {
              const active = p.plan_key === selected.plan_key;
              return (
                <Button
                  variant="ghost"
                  key={p.plan_key}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => setTab(p.plan_key)}
                  className={`min-h-9 flex-1 rounded-full px-2 text-xs font-bold transition-colors ${active ? "bg-card text-foreground shadow-xs" : "text-muted-foreground hover:text-foreground"}`}
                >
                  {ui.planName(p.plan_key)}
                </Button>
              );
            })}
          </div>
          <div className="mt-3 rounded-xl border border-border bg-card p-3">
            <div className="flex items-baseline justify-between gap-2">
              <p className="font-display text-base font-bold text-foreground">{ui.planName(selected.plan_key)}</p>
              <p className="font-body text-sm font-semibold text-foreground">
                {t("upgradePlans", "month").replace("{price}", euros(selected.price_monthly_cents))}
              </p>
            </div>
            <p className="mt-0.5 font-body text-xs text-muted-foreground">
              {t("upgradePlans", "includes")}
            </p>
            <ul className="mt-2 space-y-1.5">
              {benefits.map((g, index) => (
                <li key={g} className="flex items-start gap-2 font-body text-xs text-foreground">
                  <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-primary">
                    {selected.plan_key === "completo" && index >= 3
                      ? <Plus className="h-2.5 w-2.5 text-primary-foreground" strokeWidth={2.5} />
                      : <Check className="h-2.5 w-2.5 text-primary-foreground" strokeWidth={2.5} />}
                  </span>
                  {g}
                </li>
              ))}
            </ul>
            <Button
              className="mt-3 min-h-11 w-full gap-1.5 rounded-full font-bold"
              onClick={() => { setOpen(false); requestUpgrade(selected.plan_key as PlanKey, "header"); }}
            >
              {t("upgradePlans", "switchNow")}
              <ArrowUpRight className="h-4 w-4" strokeWidth={1.75} />
            </Button>
            <p className="mt-1.5 text-center font-body text-[11px] text-muted-foreground">
              {t("upgradePlans", "flexible")}
            </p>
          </div>
        </PopoverContent>
      </Popover>
      {dialog}
    </>
  );
}
